import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const Toggle = ({ checked, onChange, testid }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} data-testid={testid}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${checked ? "bg-primary" : "bg-slate-300"}`}>
    <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`} />
  </button>
);

const Row = ({ title, desc, children, testid }) => (
  <div className="flex flex-wrap items-center justify-between gap-4 px-5 py-4" data-testid={testid}>
    <div className="min-w-0 max-w-xl">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-600 mt-0.5">{desc}</div>
    </div>
    {children}
  </div>
);

export function ComplianceSettingsCard({ onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/compliance/settings").then((r) => setS(r.data)).catch(() => toast.error("Could not load settings")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/compliance/settings", { idle_minutes: Number(s.idle_minutes), require_otp: s.require_otp, require_totp: !!s.require_totp, retention_years: Number(s.retention_years), strip_phi_email: s.strip_phi_email, kiosk_never_idle: s.kiosk_never_idle });
      setS((prev) => ({ ...prev, ...data }));
      toast.success("Safeguards saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white" data-testid="compliance-settings">
        <Row title="Automatic logoff" desc="Staff are signed out after this many minutes without activity. A 60-second warning appears first." testid="setting-idle">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="number" min={2} max={120} value={s.idle_minutes} onChange={(e) => setS({ ...s, idle_minutes: e.target.value })} data-testid="setting-idle-minutes"
                   className="h-10 w-20 rounded-lg border border-slate-200 px-3 text-sm text-right" /> minutes
          </label>
        </Row>
        <Row title="Keep kiosk signed in" desc="The check-in kiosk screens never sign out automatically, so the station stays ready all day. Turn this off for stricter HIPAA automatic logoff." testid="setting-kiosk-idle">
          <Toggle checked={s.kiosk_never_idle !== false} onChange={(v) => setS({ ...s, kiosk_never_idle: v })} testid="setting-kiosk-never-idle" />
        </Row>
        <Row title="Two-step verification (email code)" desc="Staff must enter a 6-digit code emailed to them when signing in from a new device. Devices can be trusted for 30 days." testid="setting-otp">
          <Toggle checked={!!s.require_otp} onChange={(v) => setS({ ...s, require_otp: v })} testid="setting-require-otp" />
        </Row>
        <Row title="Require authenticator app" desc="Every staff member must enroll Google/Microsoft Authenticator or Authy before they can use the console. Anyone not yet enrolled is walked through setup at their next sign-in." testid="setting-totp">
          <Toggle checked={!!s.require_totp} onChange={(v) => setS({ ...s, require_totp: v })} testid="setting-require-totp" />
        </Row>
        <Row title="Keep health details out of email & SMS" desc="Incident and daily-report emails only say a report is ready and link to the app, instead of including the details." testid="setting-strip">
          <Toggle checked={!!s.strip_phi_email} onChange={(v) => setS({ ...s, strip_phi_email: v })} testid="setting-strip-phi-email" />
        </Row>
        <Row title="Record retention" desc="How long records and audit logs are kept. HIPAA requires at least 6 years." testid="setting-retention">
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="number" min={6} max={25} value={s.retention_years} onChange={(e) => setS({ ...s, retention_years: e.target.value })} data-testid="setting-retention-years"
                   className="h-10 w-20 rounded-lg border border-slate-200 px-3 text-sm text-right" /> years
          </label>
        </Row>
        <Row title="Encryption at rest" desc="Health profiles, incident details and care-client needs/notes are AES-encrypted before they reach the database." testid="setting-encryption">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${s.encryption_at_rest ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`} data-testid="setting-encryption-status">{s.encryption_at_rest ? "Active" : "Not configured"}</span>
        </Row>
      </div>
      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="compliance-settings-save" className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save safeguards
        </button>
      </div>
    </div>
  );
}
